/**
 * CostSpace Design Tokens
 * Single source of truth for all UI styles
 * Primary brand color from logo: #1B3A6B (dark blue)
 */

export const C = {
  // ── Background ──────────────────────────────────────────────────────────────
  pageBg:   '#F3F4F6',   // page background
  surface:  '#ffffff',   // card / panel surface
  surfaceAlt: '#F9FAFB', // table row alt, subtle bg

  // ── Border ──────────────────────────────────────────────────────────────────
  border:   '#E5E7EB',   // default border
  border2:  '#D1D5DB',   // stronger border / divider

  // ── Text ────────────────────────────────────────────────────────────────────
  ink:      '#0F1017',   // primary text
  ink2:     '#374151',   // secondary text
  sub:      '#6B7280',   // subdued / labels
  muted:    '#9CA3AF',   // placeholder / disabled

  // ── Brand (Logo Blue) ───────────────────────────────────────────────────────
  blue:     '#1B3A6B',   // primary action — buttons, links
  blueBg:   'rgba(27,58,107,.07)',
  blueLine: 'rgba(27,58,107,.2)',
  blueHover:'#152e57',   // darken 10%

  // ── Positive (Green) ────────────────────────────────────────────────────────
  green:    '#1A7A4A',   // success, on-budget, approved
  greenBg:  'rgba(26,122,74,.07)',
  greenLine:'rgba(26,122,74,.2)',

  // ── Warning (Amber) ─────────────────────────────────────────────────────────
  amber:    '#D97706',
  amberBg:  '#FEF3C7',
  amberLine:'#FDE68A',

  // ── Danger (Red) ────────────────────────────────────────────────────────────
  red:      '#DC2626',
  redBg:    '#FEF2F2',
  redLine:  '#FECACA',

  // ── Purple (EAC / Forecast / Resource Code) ─────────────────────────────────
  purple:   '#7C3AED',
  purpleBg: 'rgba(124,58,237,.06)',
  purpleLine: 'rgba(124,58,237,.2)',
}

// ── Radius ────────────────────────────────────────────────────────────────────
export const R = {
  card:   12,   // cards, panels, modals
  input:  8,    // inputs, selects, small containers
  btn:    8,    // buttons
  badge:  20,   // pills / badges
  tag:    4,    // inline tags
}

// ── Font sizes ────────────────────────────────────────────────────────────────
export const F = {
  label:    10,   // uppercase section labels
  caption:  11,   // captions, badges, table headers
  body:     12,   // default body / table cells
  bodyLg:   13,   // slightly larger body
  title:    14,   // card titles, field labels
  heading:  18,   // page headings
  kpi:      24,   // KPI numbers
  kpiLg:    32,   // hero KPI
}

// ── Spacing ───────────────────────────────────────────────────────────────────
export const S = {
  pagePad:  '20px 24px',     // page padding
  cardPad:  '16px 20px',     // card padding
  cellPad:  '9px 14px',      // table cell padding
  cellSm:   '6px 10px',      // compact table cell
}

// ── Pre-built style objects ───────────────────────────────────────────────────

/** Primary button — blue */
export const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', borderRadius: R.btn, border: 'none',
  background: C.blue, color: '#fff',
  fontSize: F.body, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit', transition: 'background .12s', whiteSpace: 'nowrap',
}
export const btnPrimaryHover = { background: C.blueHover }

/** Secondary button — white with border */
export const btnSecondary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', borderRadius: R.btn,
  border: `1px solid ${C.border2}`,
  background: C.surface, color: C.ink2,
  fontSize: F.body, fontWeight: 500, cursor: 'pointer',
  fontFamily: 'inherit', transition: 'background .12s', whiteSpace: 'nowrap',
}

/** Danger button — red */
export const btnDanger = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', borderRadius: R.btn, border: 'none',
  background: C.red, color: '#fff',
  fontSize: F.body, fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit', transition: 'background .12s', whiteSpace: 'nowrap',
}

/** Ghost button — text only, no border */
export const btnGhost = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 8px', borderRadius: R.tag,
  border: 'none', background: 'none',
  fontSize: F.body, fontWeight: 500, cursor: 'pointer',
  fontFamily: 'inherit', transition: 'color .12s',
}

/** Standard card */
export const card = {
  background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: R.card, padding: S.cardPad,
}

/** Input / select */
export const input = {
  width: '100%', padding: '8px 12px',
  border: `1px solid ${C.border2}`, borderRadius: R.input,
  fontSize: F.body, color: C.ink,
  background: C.surface, outline: 'none',
  fontFamily: 'inherit',
}

/** Table header row */
export const thStyle = {
  padding: S.cellPad, textAlign: 'left',
  fontSize: F.label, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '.07em',
  color: C.sub, borderBottom: `1px solid ${C.border}`,
  background: '#F9FAFB', whiteSpace: 'nowrap',
}

/** Page header bar */
export const pageHeader = {
  padding: '14px 24px', background: C.surface,
  borderBottom: `1px solid ${C.border}`,
  display: 'flex', alignItems: 'center', gap: 12,
}

/** Section label */
export const sectionLabel = {
  fontSize: F.label, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '.1em',
  color: C.muted,
}

/** KPI card */
export const kpiCard = {
  background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: R.card, padding: '16px 20px',
}

/** Status badge helper */
export function statusBadge(status) {
  const map = {
    active:     { bg: '#DCFCE7', color: '#15803D', border: '#BBF7D0' },
    approved:   { bg: '#DCFCE7', color: '#15803D', border: '#BBF7D0' },
    completed:  { bg: '#DBEAFE', color: '#1D4ED8', border: '#BFDBFE' },
    submitted:  { bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' },
    pending:    { bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' },
    on_hold:    { bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' },
    draft:      { bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' },
    cancelled:  { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
    rejected:   { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
  }
  return map[status] || map.draft
}

/** Toast style helper */
export function toastStyle(type) {
  return type === 'success'
    ? { background: C.green, color: '#fff' }
    : type === 'error'
    ? { background: C.red, color: '#fff' }
    : { background: C.amber, color: '#fff' }
}

/** Spinner */
export const spinner = {
  width: 22, height: 22,
  border: `3px solid ${C.border}`,
  borderTopColor: C.blue,
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
}
